import { GameScreen, GameSettings, DEFAULT_SETTINGS, ToolType, WorldInfo, ItemStack } from '../types';
import { BlockId } from '@shared/block-ids';
import { getBlock } from '../world/block-registry';
import { Inventory } from '../inventory/inventory';
import { getToolName, getToolColor, getToolSymbol } from '../inventory/tools';
import { findRecipe } from '../inventory/crafting';
import { loadSettings, saveSettings } from '../storage/settings-store';
import { getWorlds, deleteWorld, initDB } from '../storage/local-db';
import { HOTBAR_SLOTS, TEXTURE_SIZE, ATLAS_SIZE, INVENTORY_SIZE } from '../constants';

export interface UICallbacks {
  onPlayWorld: (worldId: string | null, name: string, seed: number, mode?: 'survival' | 'creative') => void;
  onJoinServer: (serverId: string) => void;
  onCreateServer: (name: string, maxPlayers: number) => void;
  onSettingsChanged: (settings: GameSettings) => void;
  onResume: () => void;
  onQuitToMenu: () => void;
}

interface ServerEntry {
  id: string;
  name: string;
  players: number;
  maxPlayers: number;
  seed: number;
}

export class UIManager {
  private uiRoot: HTMLElement;
  private hudRoot: HTMLElement;
  private currentScreen: GameScreen = GameScreen.MAIN_MENU;
  private settings: GameSettings;
  private callbacks: UICallbacks;
  private atlas: HTMLCanvasElement | null = null;
  private inventory: Inventory | null = null;
  private showDebug = false;
  private serverList: ServerEntry[] = [];

  // Crafting state
  private cursorItem: ItemStack | null = null;
  private craftingGrid: (ItemStack | null)[] = [null, null, null, null]; // 2x2
  private craftingSize = 2; // 2 for inventory, 3 for crafting table
  private cursorEl: HTMLElement | null = null;
  private isCraftingTable = false;

  // HUD elements
  private crosshairEl: HTMLElement | null = null;
  private hotbarEl: HTMLElement | null = null;
  private debugEl: HTMLElement | null = null;
  private selectedBlockEl: HTMLElement | null = null;
  private healthBarEl: HTMLElement | null = null;
  private hungerBarEl: HTMLElement | null = null;
  private breakBarEl: HTMLElement | null = null;

  constructor(callbacks: UICallbacks) {
    this.uiRoot = document.getElementById('ui-root')!;
    this.hudRoot = document.getElementById('hud-root')!;
    this.settings = loadSettings();
    this.callbacks = callbacks;
  }

  setAtlasCanvas(canvas: HTMLCanvasElement): void {
    this.atlas = canvas;
  }

  setInventory(inv: Inventory): void {
    this.inventory = inv;
  }

  setServerList(list: ServerEntry[]): void {
    this.serverList = list;
  }

  show(screen: GameScreen): void {
    this.currentScreen = screen;
    this.render();
  }

  get screen(): GameScreen {
    return this.currentScreen;
  }

  toggleDebug(): void {
    this.showDebug = !this.showDebug;
    if (this.debugEl) {
      this.debugEl.style.display = this.showDebug ? 'block' : 'none';
    }
  }

  private render(): void {
    this.uiRoot.innerHTML = '';

    switch (this.currentScreen) {
      case GameScreen.MAIN_MENU: this.renderMainMenu(); break;
      case GameScreen.WORLD_SELECT: this.renderWorldSelect(); break;
      case GameScreen.WORLD_CREATE: this.renderWorldCreate(); break;
      case GameScreen.SERVER_BROWSER: this.renderServerBrowser(); break;
      case GameScreen.SERVER_CREATE: this.renderServerCreate(); break;
      case GameScreen.SKIN_SELECT: this.renderSkinSelect(); break;
      case GameScreen.SETTINGS: this.renderSettings(); break;
      case GameScreen.LOADING: this.renderLoading(); break;
      case GameScreen.PLAYING: this.renderHUD(); break;
      case GameScreen.PAUSED: this.renderPauseMenu(); break;
      case GameScreen.INVENTORY: this.renderInventory(); break;
    }
  }

  private renderMainMenu(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h1');
    title.className = 'menu-title';
    title.textContent = 'MINERAFT';
    overlay.appendChild(title);

    const subtitle = document.createElement('p');
    subtitle.className = 'menu-subtitle';
    subtitle.textContent = 'A Browser Minecraft Clone';
    overlay.appendChild(subtitle);

    this.addButton(overlay, 'Singleplayer', () => this.show(GameScreen.WORLD_SELECT));
    this.addButton(overlay, 'Multiplayer', () => this.show(GameScreen.SERVER_BROWSER));
    this.addButton(overlay, 'Skin Select', () => this.show(GameScreen.SKIN_SELECT));
    this.addButton(overlay, 'Settings', () => this.show(GameScreen.SETTINGS));
  }

  private renderWorldSelect(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Select World';
    overlay.appendChild(title);

    const content = document.createElement('div');
    content.className = 'menu-content';

    // Load saved worlds
    const loadingText = document.createElement('p');
    loadingText.style.cssText = 'color: #aaa; text-align: center; margin: 20px 0; font-size: 14px;';
    loadingText.textContent = 'Loading worlds...';
    content.appendChild(loadingText);
    overlay.appendChild(content);

    initDB().then(() => getWorlds()).then(worlds => {
      content.innerHTML = '';
      if (worlds.length === 0) {
        const notice = document.createElement('p');
        notice.style.cssText = 'color: #aaa; text-align: center; margin: 20px 0; font-size: 14px;';
        notice.textContent = 'No saved worlds. Create a new one!';
        content.appendChild(notice);
      } else {
        worlds.sort((a, b) => b.lastPlayed - a.lastPlayed);
        for (const world of worlds) {
          const item = document.createElement('div');
          item.className = 'world-list-item';
          const date = new Date(world.lastPlayed).toLocaleDateString();
          item.innerHTML = `
            <div>
              <div class="world-name">${world.name || 'World'}</div>
              <div class="world-info">${world.gameMode} | Seed: ${world.seed} | ${date}</div>
            </div>
          `;
          item.addEventListener('click', () => {
            this.callbacks.onPlayWorld(world.id, world.name, world.seed, world.gameMode);
          });
          content.appendChild(item);
        }
      }
    });

    this.addButton(overlay, 'Create New World', () => this.show(GameScreen.WORLD_CREATE));
    this.addButton(overlay, 'Back', () => this.show(GameScreen.MAIN_MENU));
  }

  private renderWorldCreate(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Create World';
    overlay.appendChild(title);

    const nameInput = document.createElement('input');
    nameInput.className = 'input-field';
    nameInput.placeholder = 'World Name';
    nameInput.value = 'New World';
    overlay.appendChild(nameInput);

    const seedInput = document.createElement('input');
    seedInput.className = 'input-field';
    seedInput.placeholder = 'Seed (optional)';
    overlay.appendChild(seedInput);

    // Game mode selector
    let selectedMode: 'survival' | 'creative' = 'survival';
    const modeContainer = document.createElement('div');
    modeContainer.style.cssText = 'display: flex; gap: 8px; justify-content: center; margin: 12px 0;';

    const survivalBtn = document.createElement('button');
    survivalBtn.className = 'btn btn-small';
    survivalBtn.textContent = 'Survival';
    survivalBtn.style.background = '#4a6a4a';
    survivalBtn.style.borderColor = '#6a9a6a';

    const creativeBtn = document.createElement('button');
    creativeBtn.className = 'btn btn-small';
    creativeBtn.textContent = 'Creative';

    survivalBtn.addEventListener('click', () => {
      selectedMode = 'survival';
      survivalBtn.style.background = '#4a6a4a';
      survivalBtn.style.borderColor = '#6a9a6a';
      creativeBtn.style.background = '#555';
      creativeBtn.style.borderColor = '#333';
    });

    creativeBtn.addEventListener('click', () => {
      selectedMode = 'creative';
      creativeBtn.style.background = '#4a6a4a';
      creativeBtn.style.borderColor = '#6a9a6a';
      survivalBtn.style.background = '#555';
      survivalBtn.style.borderColor = '#333';
    });

    modeContainer.appendChild(survivalBtn);
    modeContainer.appendChild(creativeBtn);
    overlay.appendChild(modeContainer);

    const modeDesc = document.createElement('p');
    modeDesc.style.cssText = 'color: #aaa; text-align: center; font-size: 12px; margin-bottom: 15px;';
    modeDesc.textContent = 'Survival: Mine, craft, survive. Creative: Unlimited blocks.';
    overlay.appendChild(modeDesc);

    this.addButton(overlay, 'Create & Play', () => {
      const name = nameInput.value || 'New World';
      const seed = seedInput.value ? parseInt(seedInput.value) || Math.random() * 999999 | 0 : Math.random() * 999999 | 0;
      this.callbacks.onPlayWorld(null, name, seed, selectedMode);
    });

    this.addButton(overlay, 'Back', () => this.show(GameScreen.WORLD_SELECT));
  }

  private renderServerBrowser(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Multiplayer';
    overlay.appendChild(title);

    const content = document.createElement('div');
    content.className = 'menu-content';

    if (this.serverList.length === 0) {
      const notice = document.createElement('p');
      notice.style.cssText = 'color: #aaa; text-align: center; margin: 30px 0; font-size: 14px;';
      notice.textContent = 'No servers yet. Create one to get started!';
      content.appendChild(notice);
    } else {
      for (const server of this.serverList) {
        const item = document.createElement('div');
        item.className = 'server-list-item';
        item.innerHTML = `
          <div>
            <div class="server-name">${server.name}</div>
            <div class="server-info">Seed: ${server.seed}</div>
          </div>
          <div style="text-align: right">
            <div class="server-players">${server.players}/${server.maxPlayers}</div>
          </div>
        `;
        item.addEventListener('click', () => {
          this.callbacks.onJoinServer(server.id);
        });
        content.appendChild(item);
      }
    }

    overlay.appendChild(content);

    this.addButton(overlay, 'Create Server', () => this.show(GameScreen.SERVER_CREATE));
    this.addButton(overlay, 'Refresh', () => this.renderServerBrowser());
    this.addButton(overlay, 'Back', () => this.show(GameScreen.MAIN_MENU));
  }

  private renderServerCreate(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Create Server';
    overlay.appendChild(title);

    const nameInput = document.createElement('input');
    nameInput.className = 'input-field';
    nameInput.placeholder = 'Server Name';
    nameInput.value = 'My Server';
    overlay.appendChild(nameInput);

    const playersInput = document.createElement('input');
    playersInput.className = 'input-field';
    playersInput.placeholder = 'Max Players';
    playersInput.value = '20';
    playersInput.type = 'number';
    overlay.appendChild(playersInput);

    this.addButton(overlay, 'Create & Join', () => {
      this.callbacks.onCreateServer(
        nameInput.value || 'My Server',
        parseInt(playersInput.value) || 20,
      );
    });

    this.addButton(overlay, 'Back', () => this.show(GameScreen.SERVER_BROWSER));
  }

  private renderSkinSelect(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Select Skin';
    overlay.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'skin-grid';

    const skinColors = [
      '#8B6914', '#5C3317', '#FFA07A', '#FFD700',
      '#4169E1', '#DC143C', '#228B22', '#9400D3',
      '#FF6347', '#00CED1', '#FF69B4', '#2F4F4F',
    ];

    skinColors.forEach((color, i) => {
      const option = document.createElement('div');
      option.className = 'skin-option' + (i === 0 ? ' selected' : '');
      option.style.background = color;
      const label = document.createElement('div');
      label.style.cssText = 'color: #fff; font-size: 11px; text-shadow: 1px 1px 0 #000;';
      label.textContent = `Skin ${i + 1}`;
      option.appendChild(label);
      option.addEventListener('click', () => {
        grid.querySelectorAll('.skin-option').forEach(el => el.classList.remove('selected'));
        option.classList.add('selected');
      });
      grid.appendChild(option);
    });

    overlay.appendChild(grid);
    this.addButton(overlay, 'Back', () => this.show(GameScreen.MAIN_MENU));
  }

  private renderSettings(): void {
    this.hideHUD();
    const overlay = this.createOverlay('menu-bg');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Settings';
    overlay.appendChild(title);

    const tabs = document.createElement('div');
    tabs.className = 'settings-tabs';
    const tabNames = ['Graphics', 'Controls', 'Audio'];
    const sections: HTMLElement[] = [];

    tabNames.forEach((name, i) => {
      const tab = document.createElement('button');
      tab.className = 'settings-tab' + (i === 0 ? ' active' : '');
      tab.textContent = name;
      tab.addEventListener('click', () => {
        tabs.querySelectorAll('.settings-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        sections.forEach((s, j) => { s.style.display = j === i ? 'block' : 'none'; });
      });
      tabs.appendChild(tab);
    });
    overlay.appendChild(tabs);

    const graphicsSection = document.createElement('div');
    this.addSlider(graphicsSection, 'Render Distance', 2, 16, 1, this.settings.renderDistance, v => { this.settings.renderDistance = v; });
    this.addSlider(graphicsSection, 'FOV', 40, 110, 1, this.settings.fov, v => { this.settings.fov = v; });
    this.addSlider(graphicsSection, 'GUI Scale', 1, 4, 1, this.settings.guiScale, v => { this.settings.guiScale = v; });
    sections.push(graphicsSection);
    overlay.appendChild(graphicsSection);

    const controlsSection = document.createElement('div');
    controlsSection.style.display = 'none';
    this.addSlider(controlsSection, 'Mouse Sensitivity', 0.0005, 0.005, 0.0005, this.settings.mouseSensitivity, v => { this.settings.mouseSensitivity = v; });
    sections.push(controlsSection);
    overlay.appendChild(controlsSection);

    const audioSection = document.createElement('div');
    audioSection.style.display = 'none';
    this.addSlider(audioSection, 'Master Volume', 0, 1, 0.05, this.settings.masterVolume, v => { this.settings.masterVolume = v; });
    this.addSlider(audioSection, 'Music', 0, 1, 0.05, this.settings.musicVolume, v => { this.settings.musicVolume = v; });
    this.addSlider(audioSection, 'SFX', 0, 1, 0.05, this.settings.sfxVolume, v => { this.settings.sfxVolume = v; });
    sections.push(audioSection);
    overlay.appendChild(audioSection);

    this.addButton(overlay, 'Done', () => {
      saveSettings(this.settings);
      this.callbacks.onSettingsChanged(this.settings);
      this.show(GameScreen.MAIN_MENU);
    });
  }

  private renderLoading(): void {
    this.hideHUD();
    const overlay = this.createOverlay('');
    overlay.classList.add('loading-screen');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '28px';
    title.textContent = 'Generating World...';
    overlay.appendChild(title);

    const barContainer = document.createElement('div');
    barContainer.className = 'loading-bar-container';
    const bar = document.createElement('div');
    bar.className = 'loading-bar';
    bar.style.width = '0%';
    bar.id = 'loading-bar';
    barContainer.appendChild(bar);
    overlay.appendChild(barContainer);

    const text = document.createElement('p');
    text.className = 'loading-text';
    text.id = 'loading-text';
    text.textContent = 'Loading chunks...';
    overlay.appendChild(text);
  }

  updateLoadingProgress(progress: number, text?: string): void {
    const bar = document.getElementById('loading-bar');
    const textEl = document.getElementById('loading-text');
    if (bar) bar.style.width = `${Math.min(100, progress)}%`;
    if (textEl && text) textEl.textContent = text;
  }

  private renderHUD(): void {
    this.uiRoot.innerHTML = '';
    this.hudRoot.innerHTML = '';

    // Crosshair
    this.crosshairEl = document.createElement('div');
    this.crosshairEl.className = 'crosshair';
    this.hudRoot.appendChild(this.crosshairEl);

    // Hotbar
    this.hotbarEl = document.createElement('div');
    this.hotbarEl.className = 'hotbar';
    for (let i = 0; i < HOTBAR_SLOTS; i++) {
      const slot = document.createElement('div');
      slot.className = 'hotbar-slot' + (i === (this.inventory?.selectedSlot ?? 0) ? ' selected' : '');
      if (this.inventory) {
        const item = this.inventory.slots[i];
        if (item) {
          const icon = this.createItemIcon(item.id, item.toolId);
          if (icon) slot.appendChild(icon);
          if (!item.toolId && item.count > 1) {
            const count = document.createElement('span');
            count.className = 'item-count';
            count.textContent = String(item.count);
            slot.appendChild(count);
          }
        }
      }
      this.hotbarEl.appendChild(slot);
    }
    this.hudRoot.appendChild(this.hotbarEl);

    // Selected block name
    this.selectedBlockEl = document.createElement('div');
    this.selectedBlockEl.className = 'selected-block-indicator';
    if (this.inventory) {
      const item = this.inventory.getSelectedItem();
      this.selectedBlockEl.textContent = item ? (item.toolId ? getToolName(item.toolId) : getBlock(item.id).name) : '';
    }
    this.hudRoot.appendChild(this.selectedBlockEl);

    // Health bar (left side)
    this.healthBarEl = document.createElement('div');
    this.healthBarEl.className = 'health-bar';
    for (let i = 0; i < 10; i++) {
      const heart = document.createElement('span');
      heart.className = 'heart';
      heart.textContent = '\u2764';
      this.healthBarEl.appendChild(heart);
    }
    this.hudRoot.appendChild(this.healthBarEl);

    // Hunger bar (right side)
    this.hungerBarEl = document.createElement('div');
    this.hungerBarEl.className = 'hunger-bar';
    for (let i = 0; i < 10; i++) {
      const drum = document.createElement('span');
      drum.className = 'hunger-icon';
      drum.textContent = '\u{1F357}';
      this.hungerBarEl.appendChild(drum);
    }
    this.hudRoot.appendChild(this.hungerBarEl);

    // Break progress bar
    this.breakBarEl = document.createElement('div');
    this.breakBarEl.className = 'break-bar-container';
    this.breakBarEl.style.display = 'none';
    const breakInner = document.createElement('div');
    breakInner.className = 'break-bar';
    breakInner.id = 'break-bar-inner';
    this.breakBarEl.appendChild(breakInner);
    this.hudRoot.appendChild(this.breakBarEl);

    // Debug overlay
    this.debugEl = document.createElement('div');
    this.debugEl.className = 'debug-overlay';
    this.debugEl.style.display = this.showDebug ? 'block' : 'none';
    this.hudRoot.appendChild(this.debugEl);
  }

  updateHUD(data: {
    selectedSlot: number;
    fps: number;
    x: number; y: number; z: number;
    chunkCount: number;
    biome: string;
    timeOfDay: number;
    health: number;
    maxHealth: number;
    hunger: number;
    maxHunger: number;
    gameMode: 'survival' | 'creative';
    breakProgress: number;
  }): void {
    if (!this.hotbarEl || this.currentScreen !== GameScreen.PLAYING) return;

    // Update selected slot
    const slots = this.hotbarEl.querySelectorAll('.hotbar-slot');
    slots.forEach((slot, i) => {
      slot.classList.toggle('selected', i === data.selectedSlot);
    });

    if (this.selectedBlockEl && this.inventory) {
      const item = this.inventory.getSelectedItem();
      this.selectedBlockEl.textContent = item ? (item.toolId ? getToolName(item.toolId) : getBlock(item.id).name) : '';
    }

    // Update health
    if (this.healthBarEl) {
      const hearts = this.healthBarEl.querySelectorAll('.heart');
      const fullHearts = Math.ceil(data.health / 2);
      hearts.forEach((heart, i) => {
        if (i < fullHearts) {
          (heart as HTMLElement).style.color = '#ff3333';
          heart.textContent = '\u2764';
        } else {
          (heart as HTMLElement).style.color = '#555';
          heart.textContent = '\u2764';
        }
      });
      this.healthBarEl.style.display = data.gameMode === 'survival' ? 'flex' : 'none';
    }

    // Update hunger
    if (this.hungerBarEl) {
      const icons = this.hungerBarEl.querySelectorAll('.hunger-icon');
      const fullHunger = Math.ceil(data.hunger / 2);
      icons.forEach((icon, i) => {
        (icon as HTMLElement).style.opacity = i < fullHunger ? '1' : '0.3';
      });
      this.hungerBarEl.style.display = data.gameMode === 'survival' ? 'flex' : 'none';
    }

    // Break progress
    if (this.breakBarEl) {
      if (data.breakProgress > 0 && data.gameMode === 'survival') {
        this.breakBarEl.style.display = 'block';
        const inner = document.getElementById('break-bar-inner');
        if (inner) inner.style.width = `${data.breakProgress * 100}%`;
      } else {
        this.breakBarEl.style.display = 'none';
      }
    }

    // Debug
    if (this.debugEl && this.showDebug) {
      this.debugEl.textContent =
        `Mineraft 1.0 [${data.gameMode}]\n` +
        `FPS: ${data.fps}\n` +
        `XYZ: ${data.x.toFixed(1)} / ${data.y.toFixed(1)} / ${data.z.toFixed(1)}\n` +
        `Chunks: ${data.chunkCount}\n` +
        `Biome: ${data.biome}\n` +
        `Time: ${(data.timeOfDay * 24).toFixed(1)}h\n` +
        (data.gameMode === 'survival' ? `Health: ${data.health.toFixed(0)}/${data.maxHealth} Hunger: ${data.hunger.toFixed(1)}/${data.maxHunger}` : '');
    }
  }

  refreshHotbar(): void {
    if (this.currentScreen === GameScreen.PLAYING) {
      this.renderHUD();
    }
  }

  private renderPauseMenu(): void {
    const overlay = this.createOverlay('menu-bg-dark');
    overlay.classList.add('pause-menu');

    const title = document.createElement('h2');
    title.className = 'menu-title';
    title.style.fontSize = '32px';
    title.textContent = 'Game Paused';
    overlay.appendChild(title);

    this.addButton(overlay, 'Resume', () => this.callbacks.onResume());
    this.addButton(overlay, 'Settings', () => this.show(GameScreen.SETTINGS));
    this.addButton(overlay, 'Quit to Title', () => this.callbacks.onQuitToMenu());
  }

  openCraftingTable(): void {
    this.isCraftingTable = true;
    this.craftingSize = 3;
    this.craftingGrid = new Array(9).fill(null);
    this.show(GameScreen.INVENTORY);
  }

  private renderInventory(): void {
    const overlay = this.createOverlay('menu-bg-dark');
    overlay.classList.add('inventory-screen');
    if (!this.inventory) return;

    // Reset crafting grid if opening normal inventory
    if (!this.isCraftingTable) {
      this.craftingSize = 2;
      this.craftingGrid = [null, null, null, null];
    }

    const container = document.createElement('div');
    container.className = 'mc-inventory';

    // Title
    const title = document.createElement('div');
    title.className = 'mc-inv-title';
    title.textContent = this.isCraftingTable ? 'Crafting' : 'Inventory';
    container.appendChild(title);

    // Top section: crafting area
    const topSection = document.createElement('div');
    topSection.className = 'mc-inv-top';

    // Crafting grid
    const craftArea = document.createElement('div');
    craftArea.className = 'mc-craft-area';

    const craftLabel = document.createElement('div');
    craftLabel.className = 'mc-section-label';
    craftLabel.textContent = 'Crafting';
    craftArea.appendChild(craftLabel);

    const craftGrid = document.createElement('div');
    craftGrid.className = `mc-craft-grid mc-craft-${this.craftingSize}x${this.craftingSize}`;
    craftGrid.style.gridTemplateColumns = `repeat(${this.craftingSize}, 40px)`;

    for (let i = 0; i < this.craftingSize * this.craftingSize; i++) {
      const slot = this.createSlot(this.craftingGrid[i], () => {
        this.handleCraftSlotClick(i);
      });
      craftGrid.appendChild(slot);
    }
    craftArea.appendChild(craftGrid);

    // Arrow
    const arrow = document.createElement('div');
    arrow.className = 'mc-craft-arrow';
    arrow.textContent = '\u27A1';
    craftArea.appendChild(arrow);

    // Result slot
    const resultItem = this.getCraftingResult();
    const resultSlot = this.createSlot(resultItem, () => {
      this.handleCraftResultClick();
    });
    resultSlot.classList.add('mc-result-slot');
    craftArea.appendChild(resultSlot);

    topSection.appendChild(craftArea);
    container.appendChild(topSection);

    // Separator line
    const sep1 = document.createElement('div');
    sep1.className = 'mc-separator';
    container.appendChild(sep1);

    // Main inventory (slots 9-35)
    const mainLabel = document.createElement('div');
    mainLabel.className = 'mc-section-label';
    mainLabel.textContent = 'Inventory';
    container.appendChild(mainLabel);

    const mainGrid = document.createElement('div');
    mainGrid.className = 'mc-inv-grid';
    for (let i = HOTBAR_SLOTS; i < INVENTORY_SIZE; i++) {
      const item = this.inventory.slots[i];
      const slot = this.createSlot(item, () => {
        this.handleInvSlotClick(i);
      });
      mainGrid.appendChild(slot);
    }
    container.appendChild(mainGrid);

    // Separator
    const sep2 = document.createElement('div');
    sep2.className = 'mc-separator';
    container.appendChild(sep2);

    // Hotbar (slots 0-8)
    const hotbarLabel = document.createElement('div');
    hotbarLabel.className = 'mc-section-label';
    hotbarLabel.textContent = 'Hotbar';
    container.appendChild(hotbarLabel);

    const hotbarGrid = document.createElement('div');
    hotbarGrid.className = 'mc-inv-grid';
    for (let i = 0; i < HOTBAR_SLOTS; i++) {
      const item = this.inventory.slots[i];
      const slot = this.createSlot(item, () => {
        this.handleInvSlotClick(i);
      });
      if (i === this.inventory.selectedSlot) {
        slot.classList.add('mc-slot-selected');
      }
      hotbarGrid.appendChild(slot);
    }
    container.appendChild(hotbarGrid);

    overlay.appendChild(container);

    // Cursor item follower
    this.cursorEl = document.createElement('div');
    this.cursorEl.className = 'mc-cursor-item';
    this.cursorEl.style.display = 'none';
    overlay.appendChild(this.cursorEl);
    this.updateCursorDisplay();

    overlay.addEventListener('mousemove', (e) => {
      if (this.cursorEl) {
        this.cursorEl.style.left = (e.clientX + 8) + 'px';
        this.cursorEl.style.top = (e.clientY + 8) + 'px';
      }
    });

    // Hint
    const hint = document.createElement('div');
    hint.className = 'mc-inv-hint';
    hint.textContent = 'Click to pick up/place items | E to close';
    overlay.appendChild(hint);
  }

  private createSlot(item: ItemStack | null, onClick: () => void): HTMLElement {
    const slot = document.createElement('div');
    slot.className = 'mc-slot';
    if (item) {
      const icon = this.createItemIcon(item.id, item.toolId);
      if (icon) slot.appendChild(icon);
      if (item.count > 1) {
        const count = document.createElement('span');
        count.className = 'mc-slot-count';
        count.textContent = String(item.count);
        slot.appendChild(count);
      }
    }
    slot.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return slot;
  }

  private handleInvSlotClick(index: number): void {
    if (!this.inventory) return;
    const slotItem = this.inventory.slots[index];

    if (!this.cursorItem && !slotItem) return;

    if (!this.cursorItem) {
      // Pick up item
      this.cursorItem = { ...slotItem! };
      this.inventory.slots[index] = null;
    } else if (!slotItem) {
      // Place item
      this.inventory.slots[index] = { ...this.cursorItem };
      this.cursorItem = null;
    } else if (slotItem.id === this.cursorItem.id && slotItem.toolId === this.cursorItem.toolId && !slotItem.toolId) {
      // Stack items
      const canAdd = 64 - slotItem.count;
      const toAdd = Math.min(canAdd, this.cursorItem.count);
      slotItem.count += toAdd;
      this.cursorItem.count -= toAdd;
      if (this.cursorItem.count <= 0) this.cursorItem = null;
    } else {
      // Swap
      const temp = { ...slotItem };
      this.inventory.slots[index] = { ...this.cursorItem };
      this.cursorItem = temp;
    }

    this.renderInventory();
  }

  private handleCraftSlotClick(index: number): void {
    const slotItem = this.craftingGrid[index];

    if (!this.cursorItem && !slotItem) return;

    if (!this.cursorItem) {
      this.cursorItem = slotItem ? { ...slotItem } : null;
      this.craftingGrid[index] = null;
    } else if (!slotItem) {
      this.craftingGrid[index] = { ...this.cursorItem };
      this.cursorItem = null;
    } else if (slotItem.id === this.cursorItem.id && !slotItem.toolId) {
      const canAdd = 64 - slotItem.count;
      const toAdd = Math.min(canAdd, this.cursorItem.count);
      slotItem.count += toAdd;
      this.cursorItem.count -= toAdd;
      if (this.cursorItem.count <= 0) this.cursorItem = null;
    } else {
      const temp = { ...slotItem };
      this.craftingGrid[index] = { ...this.cursorItem };
      this.cursorItem = temp;
    }

    this.renderInventory();
  }

  private handleCraftResultClick(): void {
    const result = this.getCraftingResult();
    if (!result) return;

    if (this.cursorItem) {
      // Can only pick up if same item and stackable
      if (this.cursorItem.id === result.id && this.cursorItem.toolId === result.toolId && !result.toolId) {
        if (this.cursorItem.count + result.count > 64) return;
        this.cursorItem.count += result.count;
      } else {
        return;
      }
    } else {
      this.cursorItem = { ...result };
    }

    // Consume ingredients
    for (let i = 0; i < this.craftingGrid.length; i++) {
      const slot = this.craftingGrid[i];
      if (slot) {
        slot.count--;
        if (slot.count <= 0) this.craftingGrid[i] = null;
      }
    }

    this.renderInventory();
  }

  private getCraftingResult(): ItemStack | null {
    const size = this.craftingSize;
    const grid: (BlockId | null)[][] = [];
    for (let y = 0; y < size; y++) {
      const row: (BlockId | null)[] = [];
      for (let x = 0; x < size; x++) {
        const item = this.craftingGrid[y * size + x];
        row.push(item ? item.id : null);
      }
      grid.push(row);
    }
    const recipe = findRecipe(grid);
    return recipe ? { ...recipe.result } : null;
  }

  private updateCursorDisplay(): void {
    if (!this.cursorEl) return;
    this.cursorEl.innerHTML = '';
    if (this.cursorItem) {
      this.cursorEl.style.display = 'block';
      const icon = this.createItemIcon(this.cursorItem.id, this.cursorItem.toolId);
      if (icon) this.cursorEl.appendChild(icon);
      if (this.cursorItem.count > 1) {
        const count = document.createElement('span');
        count.className = 'mc-slot-count';
        count.textContent = String(this.cursorItem.count);
        this.cursorEl.appendChild(count);
      }
    } else {
      this.cursorEl.style.display = 'none';
    }
  }

  // Return crafting grid items to inventory on close
  returnCraftingItems(): void {
    if (!this.inventory) return;
    for (let i = 0; i < this.craftingGrid.length; i++) {
      const item = this.craftingGrid[i];
      if (item) {
        this.inventory.addItem(item.id, item.count);
        this.craftingGrid[i] = null;
      }
    }
    if (this.cursorItem) {
      this.inventory.addItem(this.cursorItem.id, this.cursorItem.count);
      this.cursorItem = null;
    }
    this.isCraftingTable = false;
  }

  private createItemIcon(blockId: BlockId, toolId?: ToolType): HTMLElement | null {
    if (toolId) {
      const el = document.createElement('div');
      el.className = 'tool-icon';
      el.textContent = getToolSymbol(toolId);
      el.style.color = getToolColor(toolId);
      return el;
    }
    if (!this.atlas) return null;
    const blockDef = getBlock(blockId);
    const texIdx = blockDef.textures.top;
    const col = texIdx % (ATLAS_SIZE / TEXTURE_SIZE);
    const row = Math.floor(texIdx / (ATLAS_SIZE / TEXTURE_SIZE));
    const canvas = document.createElement('canvas');
    canvas.className = 'item-icon';
    canvas.width = TEXTURE_SIZE;
    canvas.height = TEXTURE_SIZE;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.atlas, col * TEXTURE_SIZE, row * TEXTURE_SIZE, TEXTURE_SIZE, TEXTURE_SIZE, 0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    return canvas;
  }

  private createOverlay(...classes: string[]): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'menu-overlay ' + classes.join(' ');
    this.uiRoot.appendChild(overlay);
    return overlay;
  }

  private addButton(parent: HTMLElement, text: string, onClick: () => void): void {
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = text;
    btn.addEventListener('click', onClick);
    parent.appendChild(btn);
  }

  private addSlider(parent: HTMLElement, label: string, min: number, max: number, step: number, value: number, onChange: (v: number) => void): void {
    const container = document.createElement('div');
    container.className = 'slider-container';
    const labelEl = document.createElement('label');
    labelEl.textContent = label;
    container.appendChild(labelEl);
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    container.appendChild(input);
    const valueEl = document.createElement('span');
    valueEl.className = 'value';
    valueEl.textContent = step < 1 ? value.toFixed(2) : String(value);
    container.appendChild(valueEl);
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      valueEl.textContent = step < 1 ? v.toFixed(2) : String(v);
      onChange(v);
    });
    parent.appendChild(container);
  }

  private hideHUD(): void {
    this.hudRoot.innerHTML = '';
    this.crosshairEl = null;
    this.hotbarEl = null;
    this.debugEl = null;
    this.selectedBlockEl = null;
    this.healthBarEl = null;
    this.hungerBarEl = null;
    this.breakBarEl = null;
  }
}
